import { differenceInHours, parseISO } from 'date-fns';

const shared_sensors_utils = {
    LOOKBACK_PERIODS: {
        HALF_DAY: '12h',
        DAY: '1d',
        WEEK: '1w',
        MONTH: '1m',
        THREE_MONTHS: '3m',
        YEAR: '1y'
    },

    READING_METHOD: {
        MANUAL: 'manual',
        PROBE: 'probe',
        SENSOR: 'sensor'
    },

    DAY_IN_MILLISECONDS: 86400 * 1000,

    CENTIGRADE: '°C',
    FAHRENHEIT: '°F',

    MINUTE_THRESHOLD: 60,
    HOUR_THRESHOLD: 24,
    YEAR_THRESHOLD: 1,
    TEMPERATURE_LOG_REPORT_MAX_DAYS_INTERVAL: 30,
    TRIGGER_REPORT_MAX_DAYS_INTERVAL: 730,

    get_minimum_timestamp_for_lookback: function (lookback_period, now = Date.now()) {
        const LOOKBACK_PERIODS_MILLISECONDS = {
            [this.LOOKBACK_PERIODS.HALF_DAY]: this.DAY_IN_MILLISECONDS / 2,
            [this.LOOKBACK_PERIODS.DAY]: this.DAY_IN_MILLISECONDS,
            [this.LOOKBACK_PERIODS.WEEK]: this.DAY_IN_MILLISECONDS * 7,
            [this.LOOKBACK_PERIODS.MONTH]: this.DAY_IN_MILLISECONDS * 30,
            [this.LOOKBACK_PERIODS.THREE_MONTHS]: this.DAY_IN_MILLISECONDS * 90,
            [this.LOOKBACK_PERIODS.YEAR]: this.DAY_IN_MILLISECONDS * 365
        };
        return now - LOOKBACK_PERIODS_MILLISECONDS[lookback_period];
    },

    get_minimum_timestamp_for_sparkline: function (lookback_period_timestamp, first_reading_timestamp) {
        return Math.min(lookback_period_timestamp, first_reading_timestamp);
    },

    get_formatted_sensor_timestamp: function (options) {
        var account = options.account;
        var timestamp = options.timestamp;
        var format = options.format;
        var time_zone = options.time_zone;
        var location_time_zone = time_zone || (account && account.get('time_zone'));

        if (location_time_zone) {
            return moment(timestamp).tz(location_time_zone).format(format);
        }

        return moment(timestamp).format(format);
    },

    get_sensor_timedelta_str_repr: function (options) {
        var now_date = moment(new Date());
        var timestamp = moment(options.timestamp);

        const minutes_difference = now_date.diff(timestamp, 'minutes');
        const hours_difference = now_date.diff(timestamp, 'hours');
        const days_difference = now_date.diff(timestamp, 'days');
        const years_difference = now_date.diff(timestamp, 'years');

        if (years_difference >= this.YEAR_THRESHOLD) {
            return _t('mobile_sensors_last_reading_max_date');
        }

        // @jira:ZP-734 pluralization : i18next should automatically do this, but it's broken with our implementation
        if (hours_difference >= this.HOUR_THRESHOLD) {
            var days_translation_key =
                days_difference === 0 || days_difference > 1
                    ? 'mobile_sensors_day_count_ago_plural'
                    : 'mobile_sensors_day_count_ago';
            return _t(days_translation_key, { count: days_difference });
        }

        if (minutes_difference >= this.MINUTE_THRESHOLD) {
            var hours_translation_key =
                hours_difference === 0 || hours_difference > 1
                    ? 'mobile_sensors_hour_count_ago_plural'
                    : 'mobile_sensors_hour_count_ago';
            return _t(hours_translation_key, { count: hours_difference });
        }

        var minutes_translation_key =
            minutes_difference === 0 || minutes_difference > 1
                ? 'mobile_sensors_minute_count_ago_plural'
                : 'mobile_sensors_minute_count_ago';
        return _t(minutes_translation_key, { count: minutes_difference });
    },

    get_formatted_date_for_cooldown: function (date, account, format) {
        var _date = new Date(date);
        var time_zone = account.get('time_zone');

        if (time_zone) {
            return moment(_date).tz(time_zone).format(format);
        }
        return moment(_date).format(format);
    },

    is_valid_temperature: function (value) {
        // see the following regex link fore more details
        // https://regex101.com/r/icOKq6/1
        const REGEX_NUMBERS = /^-?\d+(,\d+)*(\.\d+(e\d+)?)?$/m;
        return REGEX_NUMBERS.test(value);
    },

    is_sensor_temperature_criteria_valid: function (temperature_criteria_value) {
        if (Backbone.$.isArray(temperature_criteria_value)) {
            return (
                this.is_valid_temperature(temperature_criteria_value[0]) &&
                this.is_valid_temperature(temperature_criteria_value[1])
            );
        }
        return this.is_valid_temperature(temperature_criteria_value);
    },

    sanitize_dev_eui: function (dev_eui) {
        if (!dev_eui) {
            return '';
        }

        // remove any ':' and space from the dev_eui
        return dev_eui.replace(/[:\s]/g, '');
    },

    isValidSensorTemperatureReading: function (isOnline, lastReadingDate, lastReadingValue) {
        if (!isOnline) {
            return false;
        }

        if (!lastReadingDate || !lastReadingValue) {
            return false;
        }

        const currentUserDate = new Date();
        const validReadingAgeInHours = 1;

        if (differenceInHours(currentUserDate, parseISO(lastReadingDate)) > validReadingAgeInHours) {
            return false;
        }

        return true;
    },

    get_sensor_reading_method_formatted: function (readingMethod) {
        if (readingMethod === this.READING_METHOD.PROBE) {
            return 'BLE';
        }

        if (readingMethod === this.READING_METHOD.MANUAL) {
            return $f.utils.capitalize_string(readingMethod);
        }

        return undefined;
    },

    get_temperature_formatted_with_reading_method(readingValue, readingMethod) {
        const readingValueFormatted = $f.utils.convert_to_users_temperature_units(readingValue);

        if ($f.utils.is_nil(readingMethod)) {
            return readingValueFormatted;
        }

        const readingMethodFormatted = $f.shared.sensors.util.get_sensor_reading_method_formatted(readingMethod);

        if (readingMethodFormatted) {
            return `${readingValueFormatted} (${readingMethodFormatted})`;
        }

        return readingValueFormatted;
    }
};

export default shared_sensors_utils;
